import request from '../request';
import { isObject, isBoolean } from '../helpers';

/**
 * Network request with appropriate headers for the JSON API
 *
 * @param  {string} uri    A URI to a requested resource
 * @param  {object} [opts] Additional options for body and HTTP method
 *
 * @return {function}    Call to `request/2`
 */
export const unauthedRequest =
  (uri, opts = {}) =>
    request(uri, {
      headers: {
        'Accept': 'application/vnd.mvbth.v1+json',
        'Content-Type': 'application/json',
      },
      ...opts,
    });

/**
 * Network request helper for unauthenticated v2 endpoints.
 *
 * @param  {string} uri    A URI to a requested resource
 * @param  {object} [opts] Additional options for body and HTTP method
 *
 * @return {function}    Call to `request/2`
 */
export const unauthedV2Request =
  (uri, opts = {}) =>
    request(uri, {
      headers: {
        'Accept': 'application/vnd.mvbth.v2+json',
        'Content-Type': 'application/json',
      },
      ...opts,
    });

/**
 * Network request helper for unauthenticated v3 endpoints.
 *
 * @param  {string} uri    A URI to a requested resource
 * @param  {object} [opts] Additional options for body and HTTP method
 *
 * @return {function}    Call to `request/2`
 */
export const unauthedV3Request =
  (uri, opts = {}) =>
    request(uri, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
      },
      ...opts,
    });

/**
 * Network request with appropriate headers for the JSON API
 *
 * @param  {string} uri       A URI to a requested resource
 * @param  {string} authToken A token used by the API to id requestor
 * @param  {object} [opts]    Additional options for body and HTTP method
 *
 * @return {function}         Call to `request/2`
 */
export const authedRequest =
  (uri, authToken, opts = {}) =>
    request(uri, {
      headers: {
        'Accept': 'application/vnd.mvbth.v2+json',
        'Content-Type': 'application/json',
        'Authorization': `Token token=${authToken}`,
      },
      ...opts,
    });

/**
 * Authenticated request helper for v3 endpoints. Mirrors `unauthedV3Request`
 * which uses `application/json` as the Accept media type rather than the
 * versioned `application/vnd.mvbth.v2+json` used by v2 endpoints.
 */
export const authedV3Request =
  (uri, authToken, opts = {}) =>
    request(uri, {
      headers: {
        'Accept': 'application/json',
        'Content-Type': 'application/json',
        'Authorization': `Token token=${authToken}`,
      },
      ...opts,
    });

export const authedFormRequest =
  (uri, authToken, opts = {}) =>
    request(uri, {
      headers: {
        'Accept': 'application/vnd.mvbth.v1+json',
        'Authorization': `Token token=${authToken}`,
      },
      ...opts,
    });

export const setFormData = (parent, obj = {}) => {
  let data = new FormData();

  Object.keys(obj).map((key) =>
    obj[key] && data.append(`${parent}[${key}]`, obj[key])
  );

  return data;
}

export const objectToFormData = (params) => {
  let data = new FormData();
  // Objects on the CURRENT recursion path, for cycle detection.
  //
  // MOVEBOOTH-REACT-B: this walker had no cycle guard, so any self-referencing
  // value produced `RangeError: Maximum call stack size exceeded` as an unhandled
  // rejection from the channel-edit save — 31 times over 6 days, invisible until
  // the e2e page-error guard attributed it. A recursive serialiser over
  // redux-form state must be cycle-safe: that state can hold DOM refs and React
  // synthetic events, both of which are circular.
  //
  // Deliberately an ANCESTOR path, not a global "seen" set. The same object
  // legitimately appearing in two SIBLING positions must serialise in both;
  // only a value that contains ITSELF is a cycle. A global set would fix the
  // crash by silently dropping the second occurrence — trading a loud failure
  // for quiet data loss, which is the worse bug.
  const ancestors = new Set();

  const setFormData = (parent, obj = {}) => {
    if (obj instanceof File) {
      data.append(parent, obj);
      return;
    }

    const canDescend = Array.isArray(obj) || isObject(obj);
    if (canDescend) {
      if (ancestors.has(obj)) {
        // Skip the cycle rather than recurse forever. Warned, never silent: a
        // dropped key here means the payload is incomplete, and a save that
        // quietly omits a field is exactly the class of bug this endpoint has
        // already produced twice.
        if (typeof console !== 'undefined' && console.warn) {
          console.warn(
            `[objectToFormData] skipped a circular reference at "${parent}" — ` +
              'this key was omitted from the request body.'
          );
        }
        return;
      }
      ancestors.add(obj);
    }

    try {
    if (Array.isArray(obj)) {
      // Rails / Rack distinguish between bare-bracket arrays (key[]=val) and
      // indexed-bracket hashes (key[0]=val). Only the bare-bracket form
      // produces an Array on the Rails side for arrays of scalars. Arrays
      // that contain any object or File (e.g. photo_filters, which mixes
      // File uploads with retained {name: url} entries) must stay in the
      // indexed-key form so Rack parses them as a Hash keyed by position —
      // mixing the two shapes in the same key breaks parsing.
      const allScalars = obj.every(
        (item) => !(item instanceof File) && (item === null || typeof item !== 'object')
      );
      obj.forEach((item, idx) => {
        if (allScalars) {
          // Note: `null` items are silently dropped here — `null == ""` is
          // false in JS, so the truthiness check below excludes them. This is
          // intentional: callers passing scalar arrays (enabled_mode_ids,
          // enabled_image_styles, etc.) should not contain nullable elements.
          // Wrap any future nullable scalar arrays in `.filter(Boolean)` at
          // the call site if you need explicit null preservation.
          if (item || item == "" || isBoolean(item)) {
            data.append(`${parent}[]`, item);
          }
        } else {
          setFormData(`${parent}[${idx}]`, item);
        }
      });
    } else if (isObject(obj)) {
      Object.keys(obj).map((key) => {
        const objValue = obj[key];
        const objKey = `${parent}[${key}]`;
        if (objValue) {
          if (isObject(objValue)) {
            setFormData(objKey, objValue);
          } else {
            data.append(objKey, objValue);
          }
        } else if (objValue == "" || isBoolean(objValue)) {
          data.append(objKey, objValue);
        }
      });
    } else if (obj || obj == "" || isBoolean(obj)) {
      data.append(parent, obj);
    }
    } finally {
      // Leaving this subtree — the object is no longer an ancestor, so a
      // sibling may legitimately reference it again.
      if (canDescend) {
        ancestors.delete(obj);
      }
    }
  };

  Object.keys(params).map((k) => setFormData(k, params[k]));

  return data;
};
